import { firstValueFrom } from 'rxjs';

import type {
  ReservationValidations,
  TripLeg,
} from '@/data/models/reservation.model';
import reservationService from '@/services/reservationService';
import triplegService from '@/services/triplegService';
import tripLegUtils from '@/services/triplegUtils';
import { getPortalLabel } from '@/hooks/usePortalLabel';

import { filterConfidenceErrorsForUnsetPayload } from '../utils/filterConfidenceErrorsForUnsetPayload';
import { filterAddressErrorsForOneSidedTripLegs } from '../utils/filterAddressErrorsForOneSidedTripLegs';
import {
  expandTripLegScoreError,
  hasBlockingTripLegConfidenceError,
  isRoundTripReturnLeg,
  mergeTripLegConfidenceResponsesIntoReservationPayload,
  mergeTripLegConfidenceResponsesIntoTripLegs,
  resolveConfidenceErrorFieldPath,
  type TripLegConfidenceErrorPayload,
  type TripLegConfidenceStoredEntry,
  type TripLegScoreCacheEntry,
} from './tripLegConfidence';

const EMPTY_VALIDATIONS: ReservationValidations = {
  tripLegUpdates: [],
  errors: [],
  warnings: [],
  log: [],
};

export type BundledValidationInput = {
  reservation: unknown;
  addressValidationEnabled: boolean;
  isClientPortal: boolean;
};

export type BundledValidationOutput = {
  validations: ReservationValidations;
  confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry>;
  /**
   * /score/trip-leg failures keyed by trip-leg index. A leg carries a LIST:
   * the 03.08.999 multi-error wrapper (e.g. both addresses invalid) is
   * expanded into its individual errors (see `expandTripLegScoreError`).
   */
  errorsByTripIndex: Record<number, TripLegConfidenceErrorPayload[]>;
};

/**
 * Address-keyed cache of in-flight / settled /score/trip-leg calls. Keyed by the
 * confidence payload (addresses only — the score is a pure function of them), so
 * concurrent or repeated runs for the same address share one network call. It's
 * module-scoped on purpose: a bundled run that gets cancelled mid-flight (e.g.
 * the estimated-pickup auto-fill firing a second FIELD_EDITED right after an
 * address change) would otherwise discard its result and the next run would
 * re-score. Cleared on form reset via `clearTripLegScoreCache`.
 */
const tripLegScoreCache = new Map<string, Promise<TripLegScoreCacheEntry>>();

export function clearTripLegScoreCache(): void {
  tripLegScoreCache.clear();
}

function scoreTripLeg(
  payload: ReturnType<typeof tripLegUtils.getTripLegConfidencePayload>,
  hasPickup: boolean,
  hasDropoff: boolean,
  // Forwarded to /score/trip-leg; part of the cache key since the response
  // differs by it (undefined = backend's per-user-category default).
  throwConfidenceError?: boolean,
): Promise<TripLegScoreCacheEntry> {
  const key = `${JSON.stringify(payload)}|throwConfidenceError=${throwConfidenceError ?? 'default'}`;
  const cached = tripLegScoreCache.get(key);
  if (cached) {
    return cached;
  }
  const promise = firstValueFrom(
    triplegService.validateTripLegAddress(
      payload,
      throwConfidenceError === undefined ? undefined : { throwConfidenceError },
    ),
  )
    .then(
      (response): TripLegScoreCacheEntry => ({
        entry: { response, hasPickup, hasDropoff },
      }),
    )
    .catch((err: unknown): TripLegScoreCacheEntry => {
      const error: TripLegConfidenceErrorPayload =
        err && typeof err === 'object'
          ? (err as TripLegConfidenceErrorPayload)
          : { message: String(err) };
      // Every /score/trip-leg error blocks. (On the client portal, low
      // confidence never errors here — throwConfidenceError=false returns it
      // as data and /validate raises LOW_CONFIDENCE_ADDRESS instead; on other
      // portals a low score throws like any other score error.) Cache
      // deterministic backend errors (they carry an errorCode), but evict
      // transient/network failures so the next run can retry them.
      if (!error.errorCode) {
        tripLegScoreCache.delete(key);
      }
      return { error };
    });
  tripLegScoreCache.set(key, promise);
  return promise;
}

function hasAddressContent(address: unknown): boolean {
  const line1 = (address as { addressLine1?: unknown } | null | undefined)
    ?.addressLine1;
  return typeof line1 === 'string' && line1.trim().length > 0;
}

/**
 * Scores a bare tripLegs array (the POST /updateTripLegs shape) via
 * /score/trip-leg and merges the resulting `confidence` / address
 * `confidenceScore` blocks into each leg — the update-path counterpart of the
 * merge the create-reservation submit performs on its payload.
 *
 * Best-effort by design: a leg whose score call fails — or that is missing
 * either address and therefore isn't scored at all — is sent unchanged
 * (`confidence` / `confidenceScore` are optional on the backend contract), so
 * the update itself still runs and any real address problem surfaces through
 * /updateTripLegs' own validation. Shares the module-level score cache, so a
 * save right after a bundled validation of the same addresses makes no extra
 * network calls.
 */
export async function scoreAndMergeConfidenceIntoTripLegs(
  tripLegs: TripLeg[],
): Promise<TripLeg[]> {
  try {
    const confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry> =
      {};
    // Client portal must receive low confidence as data, not an error, so the
    // legs still get their confidence blocks merged (the write endpoint's
    // /validate raises LOW_CONFIDENCE_ADDRESS as a warning there). Derived via
    // the non-hook portal accessor — same classification the validation path's
    // isClientPortal input uses, so the score cache keys align.
    const throwConfidenceError =
      getPortalLabel() === 'client-portal' ? false : undefined;
    await Promise.all(
      tripLegs.map(async (leg, index) => {
        const hasPickup = hasAddressContent(leg?.pickUpAddress);
        const hasDropoff = hasAddressContent(leg?.dropOffAddress);
        // /score/trip-leg rejects requests missing either address — only score
        // legs with BOTH sides present.
        if (!hasPickup || !hasDropoff) return;

        const result = await scoreTripLeg(
          tripLegUtils.getTripLegConfidencePayload(leg),
          hasPickup,
          hasDropoff,
          throwConfidenceError,
        );
        if (result.entry) {
          confidenceByTripIndex[index] = result.entry;
        }
      }),
    );
    return mergeTripLegConfidenceResponsesIntoTripLegs(
      tripLegs,
      confidenceByTripIndex,
    ) as TripLeg[];
  } catch {
    // Never block the update on a scoring problem — send the legs unscored.
    return tripLegs;
  }
}

/**
 * Single, sequential validation pass shared by every field edit:
 *  1. score every trip leg that has BOTH addresses via /score/trip-leg first
 *     (deduped/cached per address; the endpoint rejects one-sided requests);
 *  2. if any leg returns an error, skip /validate entirely — calling it without
 *     the confidence data only produces spurious address errors;
 *  3. otherwise merge the confidence data into the payload and call /validate.
 *
 * Both calls resolve before this returns, so the caller applies a single
 * aggregated result in one transition (no flickering between intermediate states).
 */
export async function runBundledValidation(
  input: BundledValidationInput,
  signal?: AbortSignal,
): Promise<BundledValidationOutput> {
  const { reservation, addressValidationEnabled, isClientPortal } = input;
  if (reservation == null) {
    throw new Error('runBundledValidation: missing reservation payload');
  }

  // Address validation disabled → legacy /validate-only behavior.
  if (!addressValidationEnabled) {
    const validations = await firstValueFrom(
      reservationService.validateReservation(reservation, signal),
    );
    return {
      validations,
      confidenceByTripIndex: {},
      errorsByTripIndex: {},
    };
  }

  const tripLegs = ((reservation as { tripLegs?: unknown[] }).tripLegs ??
    []) as Array<Record<string, unknown>>;
  const confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry> =
    {};
  const errorsByTripIndex: Record<number, TripLegConfidenceErrorPayload[]> = {};
  // Round-trip return-leg score errors are buffered separately and only
  // surfaced when no other leg errored — see the merge step after scoring.
  const returnLegErrorsByTripIndex: Record<
    number,
    TripLegConfidenceErrorPayload[]
  > = {};

  // Client portal: ask /score/trip-leg NOT to throw on a low confidence score
  // (its default for non-passenger user categories is to throw). The
  // confidence comes back as data instead, and /validate raises it as
  // LOW_CONFIDENCE_ADDRESS, which the client portal downgrades to the
  // non-blocking yellow warning — so a low-confidence address never blocks a
  // client-portal reservation. Other portals omit the param and let the
  // backend's per-user-category default apply.
  const throwConfidenceError = isClientPortal ? false : undefined;

  // 1. Score every leg first (shared cache dedupes identical addresses).
  await Promise.all(
    tripLegs.map(async (leg, index) => {
      const hasPickup = hasAddressContent(leg?.pickUpAddress);
      const hasDropoff = hasAddressContent(leg?.dropOffAddress);
      // /score/trip-leg rejects requests missing either address — only score
      // legs with BOTH sides present. Without this gate every partially
      // entered leg would produce a blocking score error and skip /validate.
      // One-sided legs go to /validate unscored; the resulting noise is
      // dropped by filterConfidenceErrorsForUnsetPayload and
      // filterAddressErrorsForOneSidedTripLegs.
      if (!hasPickup || !hasDropoff) return;

      const payload = tripLegUtils.getTripLegConfidencePayload(leg);
      const result = await scoreTripLeg(
        payload,
        hasPickup,
        hasDropoff,
        throwConfidenceError,
      );
      if (result.entry) {
        confidenceByTripIndex[index] = result.entry;
      }
      if (result.error) {
        // Stamp the target form field once, here — the only place with the leg
        // (addresses + metadata) and its index. Both the red field error (hook)
        // and the yellow warning (field component) read `formFieldPath`.
        // A 03.08.999 multi-error wrapper is expanded first so each individual
        // error resolves its own field (via its errorCodeData.addressRole) and
        // its own translation key. Round-trip return-leg errors are buffered
        // separately — whether they surface depends on the outbound leg's
        // result, which this parallel loop can't know yet.
        const target = isRoundTripReturnLeg(leg)
          ? returnLegErrorsByTripIndex
          : errorsByTripIndex;
        target[index] = expandTripLegScoreError(result.error).map((err) => ({
          ...err,
          formFieldPath: resolveConfidenceErrorFieldPath(index, leg, err),
        }));
      }
    }),
  );

  // A round trip's return leg mirrors the outbound addresses onto the same
  // single form row, so when the OUTBOUND leg also errored, the return leg's
  // errors only duplicate its feedback — an addressRole-less error defaults to
  // "dropoff", which the return-leg flip maps onto the row's OTHER field,
  // painting one bad address pair on BOTH pickup and dropoff. Suppress them in
  // that case. When the outbound leg is clean (direction-dependent score or a
  // transient failure on just the return call), surface the return-leg errors
  // instead — otherwise /validate would be skipped with nothing visible to fix.
  if (Object.keys(errorsByTripIndex).length === 0) {
    Object.assign(errorsByTripIndex, returnLegErrorsByTripIndex);
  }

  // 2. Blocking trip-leg error → skip /validate (it would surface false
  // errors). Suppressed return-leg errors are covered: they are only dropped
  // when the outbound leg has errors of its own, which block here instead.
  if (hasBlockingTripLegConfidenceError(errorsByTripIndex, isClientPortal)) {
    return {
      validations: EMPTY_VALIDATIONS,
      confidenceByTripIndex,
      errorsByTripIndex,
    };
  }

  // 3. Merge confidence and run /validate.
  const merged = mergeTripLegConfidenceResponsesIntoReservationPayload(
    reservation,
    confidenceByTripIndex,
  );
  let validations: ReservationValidations = EMPTY_VALIDATIONS;
  try {
    const result = await firstValueFrom(
      reservationService.validateReservation(merged, signal),
    );
    validations = {
      ...result,
      errors: filterAddressErrorsForOneSidedTripLegs(
        filterConfidenceErrorsForUnsetPayload(result.errors, merged),
        merged,
      ),
    };
  } catch {
    // /validate failed (e.g. backend address-lookup error). Don't lose the score
    // results — return them with no validate errors so the trip-leg warnings/
    // errors still render and Next gating stays consistent. (If the parent
    // cancelled this run, XState ignores this resolution anyway.)
  }

  return { validations, confidenceByTripIndex, errorsByTripIndex };
}
